import './verificationUploadPage.scss';

import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Fade from '@mui/material/Fade';
import Snackbar from '@mui/material/Snackbar';
import { ThemeProvider } from '@mui/material/styles';
import head from 'lodash/head';
import isEmpty from 'lodash/isEmpty';
import { FormEvent, ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { CircularProgressFullScreen } from '../../../components/CircularProgressFullScreen/CircularProgressFullScreen';
import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import useDashboardStore from '../../../store/dashboardStore/useDashboardStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import { UploadItem } from '../../../store/verificationStore/types/UploadItem';
import useVerificationStore from '../../../store/verificationStore/useVerificationStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();

  const {
    computed: { vehicleId },
  } = useDashboardStore();

  const {
    computed: { hasMissingFiles, verificationCompleted },
    activeVerification,
    fileFormatErrorMessage,
    fileSizeErrorMessage,
    isUploading,
    isUploadSuccess,
    uploadFailedMessage,
    addFile,
    clearFileErrorMessages,
    reset,
    upload,
  } = useVerificationStore();

  const activeVerificationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const previousStep = (): void => {
    navigate(`/verifications/${vehicleId}/document-list`);
  };

  useEffect((): void => {
    if (verificationCompleted) {
      previousStep();
    }
  }, [activeVerification]);

  useEffect((): (() => void) => {
    if (isUploadSuccess) {
      previousStep();
    }

    return reset;
  }, [isUploadSuccess]);

  useEffect((): (() => void) => {
    // if activeVerification couldnt be determined within 5 seconds,
    // redirect to document list to prevent endless spinner
    if (!activeVerification && !verificationCompleted) {
      const VERIFICATION_TIMEOUT = 5000;

      activeVerificationTimeoutRef.current = setTimeout((): void => {
        previousStep();
      }, VERIFICATION_TIMEOUT);
    }

    return (): void => {
      if (activeVerificationTimeoutRef.current) {
        clearTimeout(activeVerificationTimeoutRef.current);
      }
    };
  }, [activeVerification, verificationCompleted]);

  const formSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();

    if (hasMissingFiles) {
      return;
    }

    await upload(activeVerification.uploadItems);
  };

  // display a full-screen loading spinner while determining the active verification status.
  // once the active verification is available, we check whether it is already completed.
  // if verification is completed, useEffect will handle navigation back to the previous step.
  // this logic is necessary for cases where users access this page directly via URL,
  // ensuring that they are immediately redirected without briefly seeing the upload page.
  if (!activeVerification || verificationCompleted) {
    return <CircularProgressFullScreen />;
  }

  return (
    <div className="verification-upload-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          {(fileFormatErrorMessage || fileSizeErrorMessage) && (
            <Snackbar
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              autoHideDuration={5000}
              open
              onClose={clearFileErrorMessages}
            >
              <Fade in>
                <Alert
                  data-testid={locator.pages.verificationUpload.alert.fileError}
                  icon={false}
                  variant="filled"
                >
                  {fileFormatErrorMessage || fileSizeErrorMessage}
                </Alert>
              </Fade>
            </Snackbar>
          )}

          <Header showDrawerToggle onBackClick={previousStep} />

          <main>
            <form
              data-testid={locator.pages.verificationUpload.form.root}
              noValidate
              onSubmit={formSubmit}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.verificationUpload.form.h1}>
                  {activeVerification.headline}
                </h1>
                <h2 data-testid={locator.pages.verificationUpload.form.h2}>
                  {activeVerification.copy}
                </h2>

                {!isEmpty(uploadFailedMessage) && (
                  <div style={{ marginBottom: '2rem' }}>
                    <Alert
                      data-testid={
                        locator.pages.verificationUpload.alert.uploadError
                      }
                      icon={false}
                      severity="error"
                    >
                      {uploadFailedMessage}
                    </Alert>
                  </div>
                )}

                <div style={{ marginBottom: '3rem' }}>
                  <ul className="upload-item-list">
                    {activeVerification.uploadItems.map(
                      (uploadItem: UploadItem, index): ReactNode => (
                        <li
                          className={uploadItem.hasFile ? 'pending' : ''}
                          data-testid={locator.pages.verificationUpload.form.getUploadItem(
                            index,
                          )}
                          key={uploadItem.name}
                        >
                          {!uploadItem.hasFile && <span />}

                          {uploadItem.hasFile &&
                            !uploadItem.previewURL &&
                            !uploadItem.hasPDF && <UploadFileRoundedIcon />}

                          {uploadItem.hasPDF && <PictureAsPdfIcon />}

                          {uploadItem.previewURL && (
                            <img
                              alt={`Preview of ${uploadItem.fileName}`}
                              src={uploadItem.previewURL}
                            />
                          )}

                          <div className="content">
                            <div>
                              <p className="uploadItemName">
                                {uploadItem.name}
                              </p>
                              <p className="fileName">
                                {uploadItem.fileName ||
                                  t('VerificationUploadPage.required')}
                              </p>
                            </div>

                            <label>
                              {uploadItem.hasFile
                                ? t('VerificationUploadPage.edit')
                                : t('VerificationUploadPage.upload')}
                            </label>
                          </div>

                          <input
                            accept="image/png, image/jpeg, application/pdf"
                            aria-label="Upload file input"
                            type="file"
                            onChange={(event): void => {
                              addFile(uploadItem, head(event.target.files));
                            }}
                          />
                        </li>
                      ),
                    )}
                  </ul>
                </div>

                <div style={{ marginBottom: '3rem' }}>
                  <div className="inline-buttons">
                    <Button
                      color="primary"
                      data-testid={
                        locator.pages.verificationUpload.form.cancelButton
                      }
                      loadingPosition="start"
                      startIcon={<></>}
                      type="button"
                      variant="outlined"
                      fullWidth
                      onClick={previousStep}
                    >
                      {t('VerificationUploadPage.cancel')}
                    </Button>

                    <Button
                      color="primary"
                      data-testid={
                        locator.pages.verificationUpload.form.submitButton
                      }
                      disabled={hasMissingFiles}
                      loading={isUploading}
                      startIcon={<></>}
                      type="submit"
                      variant="contained"
                      fullWidth
                    >
                      {t('VerificationUploadPage.submit')}
                    </Button>
                  </div>
                </div>
              </div>
              <footer
                data-testid={locator.pages.verificationUpload.footer.root}
              >
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>

        <NavigationPane withDrawer withUserNameHeader />
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'VerificationUploadPage';
